import type { CSSProperties } from 'react';
import { useEffect } from 'react';

import { Box } from '#/components/Box/Box';
import { closeDropdownFromElementWithin } from '#/components/Dropdown/closeDropdownFromElementWithin';
import { Icon } from '#/components/Icon/Icon';
import type { IconName } from '#/components/Icon/IconName';
import { useAppConfig } from '#/config/AppConfigContext';
import { trackClicked } from '#/utils/analytics/track/trackClicked';
import { getStaticImageSrc } from '#/utils/getImageSrc';

import {
  DropdownFoldBottomMenuPanel,
  DropdownFoldMenuButtonLink,
  DropdownFoldMenuHolder,
  DropdownFoldMenuPanel,
  DropdownFoldMenuPanelButtonLinksHolder,
  DropdownFoldMenuPanelTitle,
  DropdownFoldMenuRichContentHolder,
  DropdownFoldMenuRichLink,
  DropdownFoldMenuRichLinkDescription,
  DropdownFoldMenuRichLinkTitle,
  DropdownFoldMenuRichLinksHolder,
  DropdownMobileBackdrop,
  dropdownFoldMenuButtonLinkWidthVar,
  dropdownFoldMenuColumnsCountVar,
  dropdownFoldMenuDesktopLeftVar,
} from './DropdownFoldMenu.styled';

type Props = {
  left: {
    title: string;
    width?: number;
    items: {
      iconName: IconName;
      title: string;
      href: string;
      qaId: string;
      trackClicked: { name: string; context: string };
    }[];
  };
  right:
    | {
        title: string;
        items: {
          image: string;
          imageHover: string;
          title: string;
          description: string;
          href: string;
          qaId: string;
          trackClicked: { name: string; context: string };
        }[];
      }
    | undefined;
  bottom?: React.ReactNode;
};

export function DropdownFoldMenu({ left, right, bottom }: Props) {
  const { appConfig } = useAppConfig();

  // Pre-load hover images to avoid loading flicker
  useEffect(() => {
    right?.items.forEach((item) => {
      const img = new Image();
      img.src = getStaticImageSrc(
        item.imageHover,
        appConfig.idealist.staticImgPath,
      );
    });
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <DropdownFoldMenuHolder
      style={
        {
          [dropdownFoldMenuDesktopLeftVar]: right ? '-92px' : 0,
          [dropdownFoldMenuButtonLinkWidthVar]: left.width
            ? `${left.width}px`
            : undefined,
        } as CSSProperties
      }
    >
      <Box display={['block', null, null, 'flex']} flexDirection="row">
        <DropdownFoldMenuPanel>
          <DropdownFoldMenuPanelTitle>{left.title}</DropdownFoldMenuPanelTitle>

          <DropdownFoldMenuPanelButtonLinksHolder
            style={
              {
                [dropdownFoldMenuColumnsCountVar]: Math.ceil(
                  left.items.length / 3,
                ),
              } as CSSProperties
            }
          >
            {left.items.map((item) => (
              <DropdownFoldMenuButtonLink
                key={item.title}
                to={item.href}
                qaId={item.qaId}
                onClick={(event) => {
                  trackClicked(item.trackClicked.name, {
                    context: item.trackClicked.context,
                  });
                  closeDropdownFromElementWithin(event.target as HTMLElement);
                }}
              >
                <Icon name={item.iconName} size={16} />
                <span>{item.title}</span>
              </DropdownFoldMenuButtonLink>
            ))}
          </DropdownFoldMenuPanelButtonLinksHolder>
        </DropdownFoldMenuPanel>

        {right && (
          <DropdownFoldMenuPanel>
            <DropdownFoldMenuPanelTitle>
              {right.title}
            </DropdownFoldMenuPanelTitle>

            <DropdownFoldMenuRichLinksHolder>
              {right.items.map((item) => (
                <DropdownFoldMenuRichLink
                  key={item.title}
                  to={item.href}
                  qaId={item.qaId}
                  onClick={(event) => {
                    trackClicked(item.trackClicked.name, {
                      context: item.trackClicked.context,
                    });

                    closeDropdownFromElementWithin(event.target as HTMLElement);
                  }}
                  $background={`url(${getStaticImageSrc(item.image, appConfig.idealist.staticImgPath)})`}
                  $backgroundHover={`url(${getStaticImageSrc(
                    item.imageHover,
                    appConfig.idealist.staticImgPath,
                  )})`}
                >
                  <DropdownFoldMenuRichContentHolder>
                    <DropdownFoldMenuRichLinkTitle>
                      {item.title}
                    </DropdownFoldMenuRichLinkTitle>

                    <DropdownFoldMenuRichLinkDescription>
                      {item.description}
                    </DropdownFoldMenuRichLinkDescription>
                  </DropdownFoldMenuRichContentHolder>
                </DropdownFoldMenuRichLink>
              ))}
            </DropdownFoldMenuRichLinksHolder>
          </DropdownFoldMenuPanel>
        )}
      </Box>

      {bottom && (
        <DropdownFoldBottomMenuPanel>{bottom}</DropdownFoldBottomMenuPanel>
      )}

      <DropdownMobileBackdrop
        onClick={(event) =>
          closeDropdownFromElementWithin(event.target as HTMLElement)
        }
      />
    </DropdownFoldMenuHolder>
  );
}
